/**
 * The score bar.
 *
 * Visualizes the *attackers' captured trick points* as a tug-of-war on a
 * single horizontal track. Only attackers accumulate points in Sheng Ji —
 * defenders don't score; they win the round by keeping the attackers
 * under 80. So:
 *
 *   - Marker stays put when defenders win a trick (good for defenders).
 *   - Marker moves right when attackers win a trick with points in it.
 *
 * Each zone is colored to match the table nameplates: the user's team is
 * always green, opponents are red. So if the marker is in *your* zone,
 * your side is on track to win the round.
 */

import { useState } from "react";
import type { ShengJiPublicState } from "../../../../shared/games/shengji.js";
import { useWindowWidth } from "../../lib/uiHooks.js";

interface ScoreBarProps {
  state: ShengJiPublicState;
  yourSeat: number | null;
}

const TRACK_MAX = 200;

const STOPS: { pts: number; side: "def" | "atk" }[] = [
  { pts: 35, side: "def" },
  { pts: 80, side: "def" },
  { pts: 120, side: "atk" },
  { pts: 160, side: "atk" },
  { pts: 200, side: "atk" },
];

export function ScoreBar({ state, yourSeat }: ScoreBarProps) {
  // Default collapsed on phones (matches Swoop/Regicide behavior). The
  // collapsed pill is a small button users tap to expand the bar.
  const windowWidth = useWindowWidth();
  const [open, setOpen] = useState(() => windowWidth > 700);
  // In Find-the-Friend mode a seat's team can be unrevealed (null); the
  // perspective just falls back to "neutral" until friends are found.
  const myTeam = yourSeat !== null ? (state.players[yourSeat]?.team ?? null) : null;
  const iAmDefender = myTeam !== null && myTeam === state.defendingTeam;
  const iAmAttacker = myTeam !== null && myTeam !== state.defendingTeam;

  const defenderColor = iAmDefender
    ? "var(--my-team)"
    : iAmAttacker
    ? "var(--opp-team)"
    : "var(--team-0)";
  const attackerColor = iAmAttacker
    ? "var(--my-team)"
    : iAmDefender
    ? "var(--opp-team)"
    : "var(--team-1)";

  const pts = state.attackersPoints;
  const markerPct = Math.min(100, Math.max(0, (pts / TRACK_MAX) * 100));
  const defZonePct = (80 / TRACK_MAX) * 100;
  const atkZonePct = ((TRACK_MAX - 80) / TRACK_MAX) * 100;

  if (!open) {
    return (
      <button
        className="swoop-score-collapsed"
        onClick={() => setOpen(true)}
        title="Show scoreboard"
        aria-label="Show scoreboard"
      >
        Atk {pts}/80 · Round {state.roundNumber}
      </button>
    );
  }

  return (
    <div className="score-bar" role="img" aria-label={`Attackers ${pts} of ${TRACK_MAX} points`}>
      <div className="score-bar-header">
        <div className="score-bar-side defenders" style={{ color: defenderColor }}>
          <span className="role-label">DEFENDERS{iAmDefender ? " · you" : ""}</span>
        </div>
        <div className="score-bar-center">
          <span className="muted">Round {state.roundNumber}</span>
        </div>
        <div className="score-bar-side attackers" style={{ color: attackerColor }}>
          <span className="role-label">ATTACKERS{iAmAttacker ? " · you" : ""}</span>
        </div>
        <button
          className="score-collapse-inline"
          onClick={() => setOpen(false)}
          title="Hide scoreboard"
          aria-label="Hide scoreboard"
        >
          ×
        </button>
      </div>

      <div className="score-bar-track">
        <div
          className="score-bar-zone defender"
          style={{ width: `${defZonePct}%`, background: defenderColor }}
        />
        <div
          className="score-bar-zone attacker"
          style={{ width: `${atkZonePct}%`, background: attackerColor }}
        />

        {STOPS.map((stop) => (
          <div
            key={stop.pts}
            className={`score-bar-tick ${stop.side}`}
            style={{ left: `${(stop.pts / TRACK_MAX) * 100}%` }}
          >
            <span className="tick-pts">{stop.pts}</span>
          </div>
        ))}

        <div className="score-bar-marker" style={{ left: `${markerPct}%` }}>
          <span className="marker-value">{pts}</span>
        </div>
      </div>
    </div>
  );
}
